import { useCallback, useEffect, useState } from 'react'
import type { CarteDTO, DetailLieuDTO, LieuDTO, PoseDTO, RegionDTO, SeuilDTO } from '@whatremains/shared'
import { api, ErreurApi } from '../api'
import { CarteDessinee } from '../composants/CarteDessinee'

/**
 * La carte.
 *
 * `08-INTERFACE.md` §5 : elle doit répondre à trois questions d'un coup d'œil.
 * Où suis-je · où puis-je aller et à quel prix · qu'y a-t-il là-bas.
 *
 * Un lieu non découvert **existe sans être nommé** : on sait qu'il y a quelque
 * chose, on ne sait pas quoi. Le serveur ne l'envoie pas autrement.
 */

const NOMS_TYPE: Record<LieuDTO['type'], string> = {
  capitale: 'capitale',
  ville: 'ville',
  relais: 'relais',
  ruine: 'ruine',
  donjon: 'donjon',
  port: 'port',
  point: 'lieu-dit',
}

const NOMS_DISPOSITION: Record<string, string> = {
  hostile: 'hostile',
  mefiant: 'méfiant',
  neutre: 'neutre',
  favorable: 'favorable',
  devoue: 'dévoué',
}

function Region({
  r,
  surVoyage,
  surRegarder,
  surOuvrir,
  or,
}: {
  r: RegionDTO
  surVoyage: (code: string) => void
  surRegarder: (code: string) => void
  surOuvrir: (code: string) => void
  or: number
}) {
  return (
    <div className={`region${r.ici ? ' region-ici' : ''}`}>
      <div className="region-tete">
        <h3>{r.nom}</h3>
        <div className="region-meta">
          <span>acte {r.acte}</span>
          <span>niveau {r.seuilNiveau}</span>
          <span>danger {'▮'.repeat(r.danger)}{'▯'.repeat(5 - r.danger)}</span>
          <span>{r.controle ? r.controle.nom : 'sans contrôle'}</span>
          {r.ici && <span style={{ color: 'var(--r7)' }}>tu y es</span>}
          {!r.ici && r.voisine && r.ouverte && <span>à pied</span>}
          {!r.ouverte && <span style={{ color: 'var(--r6)' }}>fermée</span>}
          {r.reste.quetesEnCours > 0 && (
            <span style={{ color: 'var(--r7)' }}>{r.reste.quetesEnCours} en cours</span>
          )}
          {r.reste.quetesDisponibles > 0 && <span>{r.reste.quetesDisponibles} à prendre</span>}
          {r.reste.lieuxNonDecouverts > 0 && (
            <span>{r.reste.lieuxNonDecouverts} à découvrir</span>
          )}
          {r.reste.quetesDisponibles === 0 &&
            r.reste.quetesEnCours === 0 &&
            r.reste.lieuxNonDecouverts === 0 && <span className="tenu">rien à y faire</span>}
        </div>
      </div>
      <p className="region-texte">{r.texte}</p>

      {/*
        Une région qu'on n'a jamais ouverte, et son prix.

        Quatrième puits d'or du canon, et le seul qui donne un objectif
        d'épargne : on ne le paie pas à la petite semaine, on met de côté pour
        lui. Le prix est écrit même quand on ne peut pas payer — une porte
        fermée sans son tarif n'est pas un arbitrage, c'est un mur.
      */}
      {!r.ouverte && (
        <p className="region-fermee">
          <span>
            Tu n’y es jamais allé. L’ouvrir coûte <b>{r.coutOuverture} or</b>
            {r.coutOuverture > or && <i> · il t’en manque {r.coutOuverture - or}</i>}
          </span>
          <button
            className="action"
            disabled={r.coutOuverture > or}
            onClick={() => surOuvrir(r.code)}
          >
            Ouvrir
          </button>
        </p>
      )}

      <div className="lieux">
        {r.lieux.map((l) => (
          <button
            key={l.code}
            className="lieu"
            disabled={!l.accessible && !l.ici}
            onClick={() => (l.ici ? surRegarder(l.code) : surVoyage(l.code))}
          >
            <span>
              <span className="lieu-type">{NOMS_TYPE[l.type]}</span>
              {l.decouvert ? (
                l.nom
              ) : (
                <span className="lieu-inconnu">quelque chose, dont tu ne sais rien</span>
              )}
            </span>
            {/*
              Combien de quêtes il y a là, lieu par lieu.

              Le compte existait par RÉGION, ce qui dit dans quel coin chercher
              et pas où aller. Enzo : « pour savoir où trouver des quêtes,
              faudrait que sur la map et la vue liste je puisse voir combien de
              quêtes sont dispos à chaque fois ». Les données étaient déjà là,
              il n'y avait qu'à les écrire.
            */}
            <span className="lieu-quetes">
              {l.quetesEnCours > 0 && (
                <em className="quetes-cours">{l.quetesEnCours} en cours</em>
              )}
              {l.quetesDisponibles > 0 && (
                <em className="quetes-dispo">{l.quetesDisponibles} à prendre</em>
              )}
            </span>
            <span className={`lieu-droite${l.ici ? ' lieu-ici' : ''}`}>
              {l.ici
                ? 'tu y es'
                : l.empechement
                  ? l.empechement
                  : l.coutVoyage > 0
                    ? `${l.coutVoyage} or`
                    : 'y aller'}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

export function Carte({ personnageId, surChangement }: { personnageId: string; surChangement: () => void }) {
  const [carte, setCarte] = useState<CarteDTO | null>(null)
  const [detail, setDetail] = useState<DetailLieuDTO | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  // La carte dessinée est la vue de base. La liste reste, elle se lit plus vite
  // quand on cherche un chiffre précis.
  const [vue, setVue] = useState<'dessin' | 'liste'>('dessin')

  /*
   * Le Seuil. Presque toujours `null`, et on ne dit jamais pourquoi.
   *
   * Interrogé ici parce que c'est le seul écran qui répond à « où suis-je »,
   * et que le Seuil ne s'ouvre qu'à un endroit et à un instant. Aucune autre
   * porte n'y mène : ni quête, ni indice, ni message. Qui n'y est pas ne voit
   * rien, et ne peut pas distinguer « pas encore » de « jamais ».
   */
  const [seuil, setSeuil] = useState<SeuilDTO | null>(null)

  /*
   * Ce qui est posé sur la pierre. Autre objet, autre panneau.
   *
   * Les confondre serait faux : le Seuil exige qu'on ait tout retiré, la pierre
   * n'exige rien du tout. C'est l'écart entre les deux qui les définit — l'un
   * choisit son porteur avec un soin extrême, l'autre ne choisit pas.
   */
  const [pose, setPose] = useState<PoseDTO | null>(null)
  /** Ce qu'on a aperçu en arrivant. Aucune explication ne suit, jamais. */
  const [apercu, setApercu] = useState<string | null>(null)

  const recharger = useCallback(async () => {
    setCarte(await api.carte(personnageId))
    setSeuil(await api.seuil(personnageId).catch(() => null))
    setPose(await api.pose(personnageId).catch(() => null))
  }, [personnageId])

  useEffect(() => { void recharger() }, [recharger])

  async function ouvrirRegion(code: string) {
    setErreur(null)
    setNote(null)
    try {
      const r = await api.ouvrirRegion(personnageId, code)
      setNote(`${r.nom} s\u2019ouvre. ${r.paye} or.`)
      await recharger()
      surChangement()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  async function voyager(code: string) {
    setErreur(null)
    setNote(null)
    try {
      const r = await api.voyager(personnageId, code)
      setNote(
        r.premiereFois
          ? `Tu arrives à ${r.nom}. Tu n’y étais jamais venu.`
          : r.coutPaye > 0
            ? `${r.nom}. ${r.coutPaye} or.`
            : `${r.nom}.`,
      )
      if (r.apercu) setApercu(r.apercu)
      await recharger()
      surChangement()
      await regarder(code)
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  async function regarder(code: string) {
    try {
      setDetail(await api.lieu(personnageId, code))
    } catch {
      setDetail(null)
    }
  }

  if (!carte) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          {carte.lieuCourant ? `${carte.lieuCourant.regionNom} · ${carte.lieuCourant.nom}` : 'Nulle part'}
        </div>
        <h1>Où</h1>
        {note && <p className="attenue">{note}</p>}
        {apercu && <p className="apercu">{apercu}</p>}
        {erreur && <p className="erreur">{erreur}</p>}
        <div className="bascule" style={{ marginTop: 8 }}>
          <button aria-pressed={vue === 'dessin'} onClick={() => setVue('dessin')}>Carte</button>
          <button aria-pressed={vue === 'liste'} onClick={() => setVue('liste')}>Liste</button>
        </div>
      </div>

      {pose && (
        <section className="seuil">
          <p className="seuil-texte">{pose.texte}</p>
          <button
            className="action"
            onClick={() =>
              void (async () => {
                setErreur(null)
                try {
                  await api.prendrePose(personnageId)
                  setNote('Tu le prends.')
                  await recharger()
                  surChangement()
                } catch (e) {
                  setErreur(e instanceof ErreurApi ? e.message : 'Rien ne se passe.')
                }
              })()
            }
          >
            Le prendre
          </button>
        </section>
      )}

      {seuil && (
        <section className="seuil">
          <p className="seuil-texte">{seuil.texte}</p>
          {seuil.consentementPret ? (
            <>
              <p className="attenue">Tu n’as plus rien sur toi.</p>
              <button
                className="action"
                onClick={() =>
                  void (async () => {
                    setErreur(null)
                    try {
                      await api.consentir(personnageId)
                      setNote('Le fermoir se referme.')
                      await recharger()
                      surChangement()
                    } catch (e) {
                      setErreur(e instanceof ErreurApi ? e.message : 'Rien ne se passe.')
                    }
                  })()
                }
              >
                Le prendre
              </button>
            </>
          ) : (
            <p className="attenue">
              Il ne se laisse pas prendre. Tu as encore {seuil.reste.join(', ')}.
            </p>
          )}
        </section>
      )}

      {vue === 'dessin' && (
        <section style={{ borderTop: 0, paddingTop: 0 }}>
          <CarteDessinee
            carte={carte}
            surLieu={(l) => (l.ici ? void regarder(l.code) : void voyager(l.code))}
          />
        </section>
      )}

      {detail && (
        <section>
          <div className="surtitre">{detail.regionNom} · {detail.lieu.nom}</div>
          <p style={{ fontSize: 16 }}>{detail.lieu.texte}</p>
          {detail.lieu.services.length > 0 && (
            <p className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.14em' }}>
              {detail.lieu.services.join('  ·  ')}
            </p>
          )}
          {detail.gens.length > 0 && (
            <div className="paires" style={{ marginTop: 18 }}>
              {detail.gens.map((g) => (
                <div className="paire" key={g.code}>
                  <b>{g.nom}</b>
                  <span>
                    {g.texte}
                    <span className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 10, marginLeft: 12 }}>
                      {NOMS_DISPOSITION[g.disposition]}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <p style={{ marginTop: 18 }}>
            <button className="discret" onClick={() => setDetail(null)}>Fermer</button>
          </p>
        </section>
      )}

      {vue === 'liste' &&
        carte.continents.map((c) => (
          <section key={c.code}>
            <div className="surtitre">{c.nom}</div>
            <p className="attenue" style={{ marginBottom: 24 }}>{c.texte}</p>
            {c.regions.map((r) => (
              <Region
                key={r.code}
                r={r}
                surVoyage={voyager}
                surRegarder={regarder}
                surOuvrir={ouvrirRegion}
                or={carte.or}
              />
            ))}
          </section>
        ))}
    </div>
  )
}
